import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import { PureQueryOptions } from '@apollo/client';

import { useTaskFromMessage, ICreatedFromMessage } from 'core/hooks';
import { Colors } from 'services/ColorService';

const PRIORITIES = ['low', 'normal', 'high'] as const;
const TITLE_MAX = 80;

/** First ~`max` chars of a message body, cut at a word boundary. */
const titleFromBody = (body: string, max = TITLE_MAX): string => {
  const clean = body.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).trim()}…`;
};

export interface ITaskSourceMessage {
  id: string;
  body: string;
  authorName: string;
  /** Slack channel — or the conversation title / mailbox for threads. */
  channelName?: string | null;
  kind?: string | null;
  sentAt: string;
}

interface ITaskFromMessageModalProps {
  message: ITaskSourceMessage;
  onClose: () => void;
  onCreated?: (msg: ICreatedFromMessage) => void;
  /** Extra queries to refetch with the mutation (feed / thread / lists). */
  refetchQueries?: PureQueryOptions[];
  /**
   * Fallback group for DM messages opened from a group feed — the created
   * task then shows up on that group's "Задачи" tab.
   */
  groupId?: string;
}

/**
 * "Create a task from a student message" dialog (AM center). Prefills the
 * title/description from the message, lets the manager pick due date, priority
 * and an assignee (ERP staff only; default = current user). The backend
 * inherits student/group from the conversation, marks the message handled and
 * links the created task back to it.
 */
export const TaskFromMessageModal = memo(
  ({
    message,
    onClose,
    onCreated,
    refetchQueries,
    groupId,
  }: ITaskFromMessageModalProps) => {
    const { t } = useTranslation();
    const { staffUsers, creating, createTask } = useTaskFromMessage();

    const [title, setTitle] = useState(() => titleFromBody(message.body));
    const [description, setDescription] = useState(() => {
      const source = message.channelName ? ` (${message.channelName})` : '';
      const when = moment(message.sentAt).format('DD.MM.YYYY HH:mm');
      return `${message.authorName}${source}, ${when}:\n> ${message.body}`;
    });
    const [dueDate, setDueDate] = useState(() => moment().format('YYYY-MM-DD'));
    const [priority, setPriority] = useState('normal');
    const [assigneeId, setAssigneeId] = useState('');

    const onOk = useCallback(async () => {
      if (!title.trim() || creating) return;
      const created = await createTask(
        {
          messageId: message.id,
          title: title.trim(),
          description: description.trim() || undefined,
          dueDate: dueDate || undefined,
          assigneeId: assigneeId || undefined,
          priority,
          groupId,
        },
        refetchQueries,
      );
      if (created) {
        if (onCreated) onCreated(created);
        onClose();
      }
    }, [
      title,
      creating,
      createTask,
      message.id,
      description,
      dueDate,
      assigneeId,
      priority,
      groupId,
      refetchQueries,
      onCreated,
      onClose,
    ]);

    return (
      <Modal
        visible
        title={t('pages.communications.taskModalTitle')}
        onOk={onOk}
        onCancel={onClose}
        confirmLoading={creating}
        okText={t('pages.tasks.add')}
        cancelText={t('button.cancel')}
        okButtonProps={{ disabled: !title.trim() }}
        destroyOnClose
      >
        <Field>
          <Label>{t('pages.communications.taskTitleLabel')}</Label>
          <TextInput
            autoFocus
            value={title}
            maxLength={255}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field>
          <Label>{t('pages.communications.taskDescriptionLabel')}</Label>
          <TextArea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Row>
          <Field>
            <Label>{t('pages.communications.taskDueLabel')}</Label>
            <TextInput
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </Field>
          <Field>
            <Label>{t('pages.communications.taskPriorityLabel')}</Label>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`pages.tasks.priority.${p}`)}
                </option>
              ))}
            </Select>
          </Field>
        </Row>
        <Field>
          <Label>{t('pages.communications.taskAssigneeLabel')}</Label>
          <Select
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
          >
            <option value="">{t('pages.communications.assigneeMe')}</option>
            {staffUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </Select>
        </Field>
      </Modal>
    );
  },
);

TaskFromMessageModal.displayName = 'TaskFromMessageModal';

const Field = styled.div`
  flex: 1;
  min-width: 0;
  margin-bottom: 12px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${Colors.Gray};
  margin-bottom: 4px;
`;

const TextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  resize: vertical;
`;

const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px;
  font: inherit;
  font-size: 13px;
  background: ${Colors.White};
`;
